/** @graphql cc **/
import gql from 'graphql-tag';
import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { fg } from '@confluence/feature-gating';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import {
	ONBOARDING_EXPERIENCES,
	NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import type { useIsEligibleForNthUserHomeOnboardingRevampLicensedUserCountQuery as useIsEligibleForNthUserHomeOnboardingRevampLicensedUserCountQuery$data } from './__types__/useIsEligibleForNthUserHomeOnboardingRevampLicensedUserCountQuery.ts';
import type { useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery as useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery$data } from './__types__/useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery.ts';

export const LicensedUserCountQuery = gql`
	query useIsEligibleForNthUserHomeOnboardingRevampLicensedUserCountQuery {
		license {
			licenseConsumingUserCount
		}
	}
`;

export const GetUserDataQuery = gql`
	query useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery {
		user: confluence_atlassianUser(current: true) {
			id
			team
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

/**
 * Custom hook to determine eligibility for nth user home onboarding revamp experiment
 *
 * Conditions includes:
 * 1. Feature gate: cc-enable-nth-user-home-onboarding-revamp must be enabled
 * 2. Whether the user is licensed (site admin or nth user)
 * 3. Whether the user is an nth user (not founding admin)
 * 4. Whether the user is on an instance with over ten users
 * 5. Whether the experience is not seen or seen within 14 days
 *
 */
export const useIsEligibleForNthUserHomeOnboardingRevamp = () => {
	const { accessStatus } = useSessionData();

	const isLicensedUser =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		Object.values({
			IS_FOUNDING_ADMIN: ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			...NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS,
		}),
		!isLicensedUser || !fg('cc-enable-nth-user-home-onboarding-revamp'),
	);

	const {
		areAllActionsDismissed,
		isFoundingAdmin,
		isTemplateListDismissed,
		templateUsageValues,
		takeoverSeenAt,
		onboardingActionStates,
	} = deserializeState(onboardingStateData);

	const isNthUser =
		isLicensedUser && !onboardingStateLoading && !onboardingStateError && !isFoundingAdmin;

	const {
		data: userCountData,
		loading: userCountDataLoading,
		error: userCountDataError,
	} = useQueryInterop<useIsEligibleForNthUserHomeOnboardingRevampLicensedUserCountQuery$data>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		LicensedUserCountQuery,
		{
			skip: !isNthUser || !fg('cc-enable-nth-user-home-onboarding-revamp'),
		},
	);

	const userCount = userCountData?.license?.licenseConsumingUserCount;
	const isOverTenUserInstance = Boolean(userCount && userCount > 10);

	const {
		data: userData,
		loading: userDataLoading,
		error: userDataError,
	} = useQueryInterop<useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery$data>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		GetUserDataQuery,
		{
			skip: !isNthUser || !fg('cc-enable-nth-user-home-onboarding-revamp'),
		},
	);

	const personalSpaceKey =
		userData?.user?.confluence?.hasPersonalSpace && userData?.user?.confluence?.space?.key;

	const isExperienceWithinFourteenDays = useMemo(() => {
		if (!takeoverSeenAt) return true;
		return differenceInDays(new Date(), new Date(takeoverSeenAt)) <= 14;
	}, [takeoverSeenAt]);

	const eligibilityError = onboardingStateError || userCountDataError || userDataError;

	if (
		eligibilityError &&
		(isUnauthorizedError(eligibilityError) || isExpectedApolloError(eligibilityError))
	) {
		markErrorAsHandled(eligibilityError);
	}

	const loading = onboardingStateLoading || userCountDataLoading || userDataLoading;
	// technically rovo_awareness_enabled has nothing to do with nthUser actions being dismissed, but rovo awareness doesn't show
	// without these checks in place. To avoid creating another FG, using the rovo awareness fg check
	const haveAllBannersBeenDismissed = fg('rovo_awareness_enabled')
		? areAllActionsDismissed && !!isTemplateListDismissed
		: false;

	const isEligible = Boolean(
		!eligibilityError &&
			!loading &&
			isNthUser &&
			isOverTenUserInstance &&
			!!personalSpaceKey &&
			!haveAllBannersBeenDismissed,
	);

	return {
		isEligible,
		isVisible: isEligible && isExperienceWithinFourteenDays,
		loading,
		error: eligibilityError,
		isTemplateListDismissed,
		templateUsageValues,
		takeoverSeenAt,
		onboardingActionStates,
	};
};
